import dayjs from 'dayjs';
import { DateTime } from "luxon";
import { changeManageB2BUserView, changeManageView } from "src/store/agent/AgentManagerSlice";
import { setHotelRoomDetailsVisibility, setReservationClientVoucherVisibility } from "src/store/reservations/slices/hotels/HotelDetailSlice";
import { setConfirmationReservation } from 'src/store/reservations/slices/ReservationsManagerSlice';
import { dateFormat } from './dateFormat';
import { IconBed, IconCar, IconToolsKitchen2 } from '@tabler/icons';
import { changeMarkupDetailsView, clearSelectedMarkup, setIsMarkupCreated } from 'src/store/markups/MarkupManagerSlice';

export {
  titles,
  GenderType,
  StatusList,
  ReservationType,
  HotelMessageLevel,
  childrenCount,
  childAge,
  ReservationStatus,
  chatConstants,
  screenSize,
  mrtViewReservationTableColumnSize,
  mrtMyReservationTableColumnSize,
  voucherTypes
} from './constants';

export const routes = {
  root: "/",
  dashboard: "/dashboard",
  locations: "/locations",
  agents: "/agents",
  hotels: "/hotels",
  services: "/services",
  b2bUsers: "/b2b-users",
  b2bMarkups: "/b2b-markups",
  b2bAgentSetup: "/b2b-agent-setup",
  chat: "/chat",
  reservations: {
    base: "/reservations",
    new: "/reservations/new",
    my: "/reservations/my",
    overview: ":id/overview",
    allClientVouchers: ":reservationId/client-voucher/:voucherId",
    singleClientVoucher: ":reservationId/client-voucher/:voucherId/voucher-items/:itemReference",
  },
  auth: {
    error404: "/auth/404",
    maintenance: "/auth/maintenance",
  },
};

export const permissions = {
  dashboard: "dashboard",
  locations: "locations",
  agents: "agents",
  hotels: "hotels",
  services: "services",
  b2bUsers: "admin-b2b-users",
  b2bMarkups: "markups",
  b2bAgentSetup: "admin-b2b-agent-setup",
  chat: "chat",
  b2bNewReservations: "b2b-new-reservation",
  b2bMyReservations: "b2b-my-reservations",
  backOfficeViewReservations: "bo-view-reservations",
};

export const formatLocalTime = (value) => {
  const utcDate = DateTime.fromISO(value, { zone: "utc" });
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localDate = utcDate.setZone(userTimeZone);
  return localDate.toFormat(`${dateFormat} HH:mm:ss A`);
};

export const formatLocalDate = (dateString) => {
  if (!dateString) return null;

  let parsedDate = dayjs(dateString, dateFormat, true);
  if (!parsedDate.isValid()) {
    parsedDate = dayjs(dateString);
  }

  return parsedDate.isValid() ? parsedDate.format(dateFormat) : null;
};

export const notAvailableValueFormatter = (value) => {
  return value && value !== "" ? value : "N/A";
};

export const generateAvatar = (name, size = 100) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#2188c2";
  ctx.fillRect(0, 0, size, size);

  ctx.font = `${size / 2.5}px Arial`;
  ctx.fillStyle = "#FFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  ctx.fillText(initials, size / 2, size / 2);

  return canvas.toDataURL();
};

export const manageMenuItemChange = (value, dispatch) => {
  switch (value) {
    case routes.reservations.my:
      return dispatch(setReservationClientVoucherVisibility(false));
    case routes.b2bAgentSetup:
      return dispatch(changeManageView(false));
    case routes.reservations.new:
      return (
        dispatch(setHotelRoomDetailsVisibility(false)),
        dispatch(setConfirmationReservation(false))
      );
    case routes.b2bUsers:
      return dispatch(changeManageB2BUserView(false));
    case routes.b2bMarkups:
      return (
        dispatch(clearSelectedMarkup()),
        dispatch(changeMarkupDetailsView(false)),
        dispatch(setIsMarkupCreated(false))
      );
    default:
      return;
  }
};

export const getChipColor = (availability) => {
  switch (availability) {
    case "Available":
      return "success";
    case "WaitListed":
      return "success";
    case "OnRequest":
      return "warning";
    case "Partial":
      return "error";
    default:
      return "primary";
  }
};

export const getTypeConfig = (theme) => ({
  Hotels: {
    icon: <IconBed />,
    color: theme.palette.primary.main,
    label: 'Hotels',
    bgColor: `${theme.palette.primary.light}20`,
    displayName: 'Hotel Name',
    nameKey: 'PropertyName',
    type: "hotel"
  },
  Services: {
    icon: <IconToolsKitchen2 />,
    color: theme.palette.success.main,
    label: 'Services',
    bgColor: `${theme.palette.success.light}20`,
    displayName: 'Service Name',
    nameKey: 'ServiceName',
    type: "service"
  },
  Transfers: {
    icon: <IconCar />,
    color: theme.palette.error.main,
    label: 'Transfers',
    bgColor: `${theme.palette.error.light}20`,
    displayName: 'Transfer Name',
    nameKey: 'TransferName',
    type: "transfer"
  },
});

export const getReservationStatusChipColor = (status) => {
  switch (status) {
    case "Confirmed":
      return "primary";
    case "WaitListed":
      return "success";
    case "Cancelled":
      return "error";
    default:
      return "default";
  }
};

export const formatNumericValue = (val, isNumeric = false, decimalPlaces = 2) => {
  if (!isNumeric || !val) return val;
  const numValue = parseFloat(val);
  return isNaN(numValue) ? val : numValue.toFixed(decimalPlaces);
};